/** Modal for publishing / unpublishing one or many brand items across the brand's storefronts. */
import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {
   Alert,
   Button,
   Checkbox,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   FormControlLabel,
   FormGroup,
   LinearProgress
} from "@mui/material";
import {useBrandItemPublishItemsQuery, useBrandItemsPublishMutation} from "../../Queries";
import {changedShops, nextPublishState, PublishItem, ShopPublishState, shopPublishState} from "./publishRules";

type PublishShop = {
   _id: string
   name: string
}

type BrandItemPublishDialogProps = {
   brandItemIds: string[]
   /** The storefronts offered as checkboxes. */
   shops: PublishShop[]
   open: boolean
   onClose: () => void
   /** Fired once the publishing was saved, so the caller can reload. */
   onCompleted?: () => void
}

/**
 * Shows every storefront as a checkbox, pre-set from the selected items' current
 * publishing (indeterminate when they disagree), and sends only the changed
 * storefronts to Core in a single BrandItemsPublish call.
 */
export const BrandItemPublishDialog = ({brandItemIds, shops, open, onClose, onCompleted}: BrandItemPublishDialogProps) => {
   const {data, loading} = useBrandItemPublishItemsQuery({
      variables: {
         brandItemIds
      },
      skip: !open || !brandItemIds.length,
      fetchPolicy: 'network-only'
   });
   const [publishItems] = useBrandItemsPublishMutation();

   const items = useMemo(() => {
      return (data?.BrandItems || []).filter((item): item is PublishItem => !!item);
   }, [data]);

   const initial = useMemo(() => {
      return Object.fromEntries(shops.map(shop => [shop._id, shopPublishState(items, shop._id)]));
   }, [items, shops]);

   const [desired, setDesired] = useState<Record<string, ShopPublishState>>({});
   const [busy, setBusy] = useState(false);
   const [error, setError] = useState<string | undefined>();

   // Keyed on the loaded data, not `initial`: callers may pass a fresh shops array
   // every render, which must not wipe the user's unsaved choices.
   useEffect(() => {
      if (open && data) {
         setDesired(initial);
         setError(undefined);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open, data]);

   const changes = changedShops(initial, desired);

   const onToggle = (shopId: string) => {
      setDesired(prev => ({...prev, [shopId]: nextPublishState(prev[shopId], initial[shopId])}));
   }

   const onSave = async () => {
      setBusy(true);
      setError(undefined);
      try {
         await publishItems({variables: {brandItemIds, shops: changes}});
         onCompleted?.();
         onClose();
      } catch (e) {
         setError((e as Error).message || 'Publishing could not be updated');
      } finally {
         setBusy(false);
      }
   }

   return <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth={"xs"} fullWidth>
      <DialogTitle>Edit publishing for {brandItemIds.length} item{brandItemIds.length === 1 ? '' : 's'}</DialogTitle>
      <DialogContent>
         {loading && <LinearProgress/>}
         {!loading && <FormGroup>
            {shops.map(shop => {
               const state = desired[shop._id];
               return <FormControlLabel
                  key={shop._id}
                  label={shop.name}
                  disabled={busy}
                  control={<Checkbox
                     checked={state === ShopPublishState.Published}
                     indeterminate={state === ShopPublishState.Mixed}
                     onChange={() => onToggle(shop._id)}
                  />}
               />
            })}
         </FormGroup>}

         {busy && <LinearProgress sx={{mt: 2}}/>}

         {error && <Alert severity={"error"} sx={{mt: 2}}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
         <Button onClick={onClose} disabled={busy}>Cancel</Button>
         <Button variant={"contained"} disabled={busy || loading || !changes.length} onClick={onSave}>
            Save
         </Button>
      </DialogActions>
   </Dialog>
}
